import React, { useState, useEffect } from "react";
import { TargetSelect } from "./TargetSelect";

const DarkWebScanner = () => {
  const [domain, setDomain] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("form");
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/scans/dark-web", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (err) {
      console.error("Error fetching dark web scans:", err);
    }
  };

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (email && !emails.includes(email) && email.includes("@")) {
      setEmails([...emails, email]);
      setEmailInput("");
    }
  };

  const removeEmail = (emailToRemove) => {
    setEmails(emails.filter((e) => e !== emailToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const handleScan = async () => {
    if (!domain) {
      setError("Por favor ingrese el dominio de su empresa");
      return;
    }
    if (emails.length === 0) {
      setError("Por favor agregue al menos un email para verificar");
      return;
    }

    setScanning(true);
    setError(null);
    setResults(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/scans/dark-web", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ domain, emails }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Error en el escaneo");
      }

      const data = await response.json();
      setResults(data.results);
      fetchScans();
    } catch (err) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const loadScanDetails = async (scanId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/scans/dark-web/${scanId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedScan(data);
        setResults(data.results);
        setViewMode("results");
      }
    } catch (err) {
      console.error("Error loading scan details:", err);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "critical":
        return "text-red-600 bg-red-100";
      case "high":
        return "text-orange-600 bg-orange-100";
      case "medium":
        return "text-yellow-600 bg-yellow-100";
      case "low":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getRiskLabel = (level) => {
    switch (level) {
      case "critical":
        return "CRÍTICO";
      case "high":
        return "ALTO";
      case "medium":
        return "MEDIO";
      case "low":
        return "BAJO";
      default:
        return level;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("es-AR", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <span className="text-4xl">🌑</span>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Scanner Dark Web
            </h2>
            <p className="text-gray-500">
              Detecta credenciales expuestas en brechas de datos
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode("form")}
            className={`px-4 py-2 rounded-lg transition-colors ${
              viewMode === "form"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            🔍 Nuevo Escaneo
          </button>
          <button
            onClick={() => setViewMode("history")}
            className={`px-4 py-2 rounded-lg transition-colors ${
              viewMode === "history"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            📋 Historial ({scans.length})
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-6">
        <div className="flex items-start">
          <span className="text-2xl mr-3">🔐</span>
          <div>
            <p className="text-purple-800 font-medium">
              ¿Qué detecta este scanner?
            </p>
            <ul className="text-purple-700 text-sm mt-1 space-y-1">
              <li>• Emails corporativos en brechas conocidas (LinkedIn, Adobe, etc.)</li>
              <li>• Contraseñas expuestas en filtraciones</li>
              <li>• Tipos de datos comprometidos (teléfonos, direcciones, etc.)</li>
              <li>• Score de riesgo para evaluación de seguros cyber</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Form View */}
      {viewMode === "form" && (
        <div className="space-y-6">
          {/* Domain: solo objetivos con autorizacion vigente */}
          <TargetSelect
            label="Dominio de la Empresa"
            value={domain}
            onChange={setDomain}
            types={['domain']}
            disabled={scanning}
            testid="darkweb-target-select"
          />

          {/* Email Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Emails a Verificar
            </label>
            <div className="flex space-x-2">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="usuario@empresa.com.ar"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                onClick={addEmail}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Agregar
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Presione Enter o click en Agregar para añadir el email
            </p>
          </div>

          {/* Email List */}
          {emails.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Emails a verificar ({emails.length}):
              </p>
              <div className="flex flex-wrap gap-2">
                {emails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                  >
                    {email}
                    <button
                      onClick={() => removeEmail(email)}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Emails */}
          {domain && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-blue-800 mb-2">
                💡 Emails sugeridos para verificar:
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  `admin@${domain}`,
                  `info@${domain}`,
                  `contacto@${domain}`,
                  `rrhh@${domain}`,
                  `sistemas@${domain}`,
                ].map((suggested) => (
                  <button
                    key={suggested}
                    onClick={() => {
                      if (!emails.includes(suggested)) {
                        setEmails([...emails, suggested]);
                      }
                    }}
                    disabled={emails.includes(suggested)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      emails.includes(suggested)
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                    }`}
                  >
                    + {suggested}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Scan Button */}
          <button
            onClick={handleScan}
            disabled={scanning || !domain || emails.length === 0}
            className={`w-full py-4 rounded-lg text-white font-medium text-lg transition-colors ${
              scanning || !domain || emails.length === 0
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            {scanning ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Escaneando... (esto puede tomar unos minutos)
              </span>
            ) : (
              "🔍 Iniciar Escaneo Dark Web"
            )}
          </button>
        </div>
      )}

      {/* History View */}
      {viewMode === "history" && (
        <div className="space-y-4">
          {scans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <span className="text-4xl mb-4 block">📭</span>
              <p>No hay escaneos previos</p>
              <button
                onClick={() => setViewMode("form")}
                className="mt-4 text-purple-600 hover:underline"
              >
                Realizar primer escaneo
              </button>
            </div>
          ) : (
            scans.map((scan) => (
              <div
                key={scan.id}
                onClick={() => loadScanDetails(scan.id)}
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-800">
                      {scan.config?.domain || "Dominio no especificado"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(scan.created_at)} •{" "}
                      {scan.config?.email_count || 0} emails escaneados
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(
                        scan.summary?.risk_level
                      )}`}
                    >
                      {scan.summary?.risk_score || 0}/100
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {scan.summary?.emails_exposed || 0} expuestos
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Results View */}
      {viewMode === "results" && results && (
        <div className="space-y-6">
          <button
            onClick={() => {
              setViewMode("form");
              setResults(null);
            }}
            className="text-purple-600 hover:underline flex items-center"
          >
            ← Volver al formulario
          </button>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-gray-800">
                {results.emails_scanned}
              </p>
              <p className="text-sm text-gray-500">Emails Escaneados</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-red-600">
                {results.emails_exposed}
              </p>
              <p className="text-sm text-gray-500">Emails Expuestos</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-orange-600">
                {results.total_breaches}
              </p>
              <p className="text-sm text-gray-500">Brechas Detectadas</p>
            </div>
            <div
              className={`p-4 rounded-lg text-center ${getRiskColor(
                results.risk_level
              )}`}
            >
              <p className="text-3xl font-bold">{results.risk_score}/100</p>
              <p className="text-sm">
                Riesgo {getRiskLabel(results.risk_level)}
              </p>
            </div>
          </div>

          {/* Recommendations */}
          {results.recommendations && results.recommendations.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h3 className="font-medium text-yellow-800 mb-2">
                📋 Recomendaciones
              </h3>
              <ul className="space-y-2">
                {results.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-yellow-700 text-sm">
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Exposures Detail */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-4">
              Detalle por Email
            </h3>
            <div className="space-y-4">
              {results.exposures?.map((exposure, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border ${
                    exposure.is_exposed
                      ? "border-red-200 bg-red-50"
                      : "border-green-200 bg-green-50"
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center space-x-2">
                      <span>{exposure.is_exposed ? "⚠️" : "✅"}</span>
                      <span className="font-medium">{exposure.email}</span>
                    </div>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(
                        exposure.risk_level
                      )}`}
                    >
                      {getRiskLabel(exposure.risk_level)}
                    </span>
                  </div>

                  {exposure.is_exposed ? (
                    <div className="mt-3">
                      <p className="text-sm text-red-700 mb-2">
                        Encontrado en {exposure.breach_count} brecha(s)
                      </p>

                      {exposure.exposed_data_types?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-600 mb-1">
                            Datos expuestos:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {exposure.exposed_data_types.map((type, i) => (
                              <span
                                key={i}
                                className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs"
                              >
                                {type}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {exposure.breaches?.length > 0 && (
                        <details className="mt-2">
                          <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                            Ver brechas ({exposure.breaches.length})
                          </summary>
                          <div className="mt-2 space-y-2">
                            {exposure.breaches.map((breach, i) => (
                              <div
                                key={i}
                                className="p-2 bg-white rounded border text-sm"
                              >
                                <div className="flex justify-between">
                                  <span className="font-medium">
                                    {breach.name}
                                  </span>
                                  <span className="text-gray-500">
                                    {formatDate(breach.breach_date)}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  {breach.pwn_count?.toLocaleString()} cuentas
                                  afectadas
                                </p>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-green-700">
                      ✓ No se encontraron exposiciones en brechas conocidas
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Show results after scan */}
      {viewMode === "form" && results && (
        <div className="mt-6">
          <button
            onClick={() => setViewMode("results")}
            className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            📊 Ver Resultados del Escaneo
          </button>
        </div>
      )}
    </div>
  );
};

export default DarkWebScanner;
